/**
 * AI Feedback — what people said about what the models produced.
 *
 * The same three screens as the Feedback module, over the same two collections,
 * filtered to the component types that describe a model's output: a prompt
 * execution, a RAG answer, an agent run, a graded eval case, an extracted
 * document.
 *
 * Why a second screen rather than a filter on the first: the two audiences do
 * not overlap. A prompt owner reading complaints about a wrong answer and an
 * engineer reading complaints about a broken screen are looking for different
 * things, and one merged queue is a queue where each scrolls past the other's
 * work. What is NOT duplicated is the machinery — one submission collection, one
 * review row per reviewer, one set of outcomes, one inbox query, one set of
 * permissions. `scope="AI"` is the whole difference, and the server applies the
 * same split on its side (`feedback_component_scope.py`) so the two windows can
 * never disagree about what counts as AI.
 *
 * Definitions are split the same way: the AI forms are authored here and the
 * screen/API forms in the Feedback module, each offering only its own half of
 * `feedbackComponentType`.
 */
import GroupScreen from '../../components/layout/group-screen';
import { JOB_NAMES } from '../../constants/jobNames';
import FeedbackMasterList from '../feedback-master/feedback-master-list';
import SubmissionsTab from '../feedback/tabs/submissions-tab';
import ReviewsTab from '../feedback/tabs/reviews-tab';

const AiFeedbackGroup = () => (
  <GroupScreen
    icon="message-dots"
    iconBgColor="bg-fuchsia-600"
    title="AI Feedback"
    subtitle="What people said about the answers, and what was done about it"
    testId="SCR-AiFeedback"
    tabs={[
      {
        // First, because it is where the work starts: nothing reaches the other
        // two tabs until a form exists to be answered.
        key: 'definitions', header: 'AI Feedback Forms', leftIcon: 'message-dots',
        jobs: [JOB_NAMES.FEEDBACK_MASTER],
        element: <FeedbackMasterList scope="AI" />,
      },
      {
        // The same three permissions as the Feedback module, deliberately: being
        // allowed to read AI feedback is not a different grant from being allowed
        // to read feedback, and minting one would mean a migration, a seed and a
        // second thing to get wrong when an SME is onboarded.
        key: 'submissions', header: 'AI Submissions', leftIcon: 'message-text',
        jobs: [JOB_NAMES.FEEDBACK_SUBMISSION],
        element: <SubmissionsTab scope="AI" />,
      },
      {
        key: 'reviews', header: 'AI Reviews', leftIcon: 'message-check',
        jobs: [JOB_NAMES.FEEDBACK_REVIEW],
        element: <ReviewsTab scope="AI" />,
      },
    ]}
  />
);

export default AiFeedbackGroup;
